import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  IconButton,
  Autocomplete,
  Tooltip,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink } from 'react-router-dom';
import {
  customerOrderService,
  customerDirectoryService,
  productService,
  finishedGoodsBalancesService,
  productKitService,
} from '../services/api';

/** Підпис у полі «Виріб»: назва без дублю артикулу, якщо в `name` збережено «ART - …» / «ART — …». */
function productLabelWithoutLeadingArticle(name, article) {
  const n = String(name ?? '').trim();
  const a = String(article ?? '').trim();
  if (!n) return '';
  if (!a) return n;
  const escaped = a.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const stripped = n.replace(new RegExp(`^${escaped}\\s*[-\\u2013\\u2014]\\s*`, 'i'), '').trim();
  return stripped || n;
}

const CALC_FORM_OPTIONS = [
  { value: 'form1', label: 'Форма 1' },
  { value: 'form2', label: 'Форма 2' },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: 'deferred', label: 'Відтермінування' },
  { value: 'full', label: 'Повна оплата' },
];

let lineKeySeq = 0;
const nextLineKey = () => {
  lineKeySeq += 1;
  return `ln${lineKeySeq}`;
};

const emptyLineRow = () => ({
  tempKey: nextLineKey(),
  id: null,
  product: '',
  product_article: '',
  quantity: '',
  color: '',
  price: '',
  shipped_quantity: 0,
  production_status_display: '',
  to_ship_allocations: {},
  to_ship: 0,
});

/** Сьогодні у форматі YYYY-MM-DD для input type="date". */
function todayDateInput() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** ISO з API -> YYYY-MM-DD у локальному календарі. */
function isoToDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** YYYY-MM-DD -> ISO для API (полудень локально, щоб не зсувався день через часові пояси). */
function dateInputToIso(dateStr) {
  const parts = String(dateStr || '').trim().split('-');
  if (parts.length !== 3) return new Date().toISOString();
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return new Date().toISOString();
  return new Date(y, m - 1, d, 12, 0, 0, 0).toISOString();
}

function formatOrderDateOnly(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('uk-UA');
}

function formatOrderAmount(value) {
  if (value == null) return '—';
  return value.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const CustomerOrdersPage = forwardRef(function CustomerOrdersPage(_props, ref) {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerCache, setCustomerCache] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [productionStatus, setProductionStatus] = useState('');
  const [shipmentStatus, setShipmentStatus] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [priorOrdersDialogOpen, setPriorOrdersDialogOpen] = useState(false);
  const [kitDialogOpen, setKitDialogOpen] = useState(false);
  const [kits, setKits] = useState([]);
  const [kitMultiplier, setKitMultiplier] = useState({});
  const [priorOrdersLoading, setPriorOrdersLoading] = useState(false);
  const [priorOrdersList, setPriorOrdersList] = useState([]);
  const priorOrdersReqRef = useRef(0);
  const [lineRows, setLineRows] = useState([emptyLineRow()]);
  const [kitPackaging, setKitPackaging] = useState([]);
  const [appliedKitInfo, setAppliedKitInfo] = useState(null); // { kitId, mult, totalQtyInKit }
  const [productPickerOpenKey, setProductPickerOpenKey] = useState(null);
  const [fgBalances, setFgBalances] = useState({ by_product: [], by_order_product: [] });
  const [fgBalancesLoading, setFgBalancesLoading] = useState(false);
  const [materialShortagePreview, setMaterialShortagePreview] = useState({
    byLine: {},
    shortages: [],
    sufficient: true,
    loading: false,
    purchaseRequestId: null,
  });
  const materialShortageTimerRef = useRef(null);
  const [stockDetailOpen, setStockDetailOpen] = useState(false);
  const [stockDetailProductId, setStockDetailProductId] = useState(null);
  const [stockDetailLabel, setStockDetailLabel] = useState('');
  const [stockDetailRowTempKey, setStockDetailRowTempKey] = useState(null);
  const [shipDraft, setShipDraft] = useState({});
  const [form, setForm] = useState({
    number: '',
    customer: '',
    customer_name: '',
    order_date: todayDateInput(),
    deadline: '',
    payment_type: 'cash',
    calc_form: 'form1',
    payment_method: '',
    shipping_method: 'self',
    unloading_address: '',
    warehouse: '',
    comment: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!dialogOpen) return undefined;
    let cancelled = false;
    setFgBalancesLoading(true);
    finishedGoodsBalancesService
      .getBalances()
      .then((res) => {
        if (!cancelled) {
          setFgBalances(res.data || { by_product: [], by_order_product: [] });
        }
      })
      .catch(() => {
        if (!cancelled) setFgBalances({ by_product: [], by_order_product: [] });
      })
      .finally(() => {
        if (!cancelled) setFgBalancesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dialogOpen]);

  useEffect(() => {
    if (!dialogOpen) {
      if (materialShortageTimerRef.current) {
        clearTimeout(materialShortageTimerRef.current);
        materialShortageTimerRef.current = null;
      }
      setMaterialShortagePreview({
        byLine: {},
        shortages: [],
        sufficient: true,
        loading: false,
        purchaseRequestId: null,
      });
      return undefined;
    }

    const linesPayload = lineRows
      .filter((r) => r.product && String(r.quantity || '').trim() !== '')
      .map((r) => {
        const q = parseInt(String(r.quantity).replace(/\s/g, ''), 10);
        return {
          tempKey: r.tempKey,
          product: r.product,
          quantity: Number.isFinite(q) && q >= 1 ? q : 0,
        };
      })
      .filter((l) => l.quantity >= 1);

    if (linesPayload.length === 0) {
      if (materialShortageTimerRef.current) {
        clearTimeout(materialShortageTimerRef.current);
        materialShortageTimerRef.current = null;
      }
      setMaterialShortagePreview({
        byLine: {},
        shortages: [],
        sufficient: true,
        loading: false,
        purchaseRequestId: null,
      });
      return undefined;
    }

    let cancelled = false;
    setMaterialShortagePreview((prev) => ({ ...prev, loading: true }));

    if (materialShortageTimerRef.current) {
      clearTimeout(materialShortageTimerRef.current);
    }
    materialShortageTimerRef.current = setTimeout(() => {
      customerOrderService
        .materialShortagePreview(linesPayload, {
          persistPurchaseRequest: Boolean(editing),
          customerOrderId: editing,
        })
        .then((res) => {
          if (cancelled) return;
          const data = res.data || {};
          setMaterialShortagePreview({
            byLine: data.by_line || {},
            shortages: Array.isArray(data.shortages) ? data.shortages : [],
            sufficient: data.sufficient !== false,
            loading: false,
            purchaseRequestId: data.purchase_request_id ?? null,
          });
        })
        .catch(() => {
          if (cancelled) return;
          setMaterialShortagePreview({
            byLine: {},
            shortages: [],
            sufficient: true,
            loading: false,
            purchaseRequestId: null,
          });
        });
    }, 400);

    return () => {
      cancelled = true;
      if (materialShortageTimerRef.current) {
        clearTimeout(materialShortageTimerRef.current);
        materialShortageTimerRef.current = null;
      }
    };
  }, [dialogOpen, lineRows, editing]);

  const stockByProductId = useMemo(() => {
    const m = {};
    (fgBalances.by_product || []).forEach((r) => {
      m[Number(r.product_id)] = Number(r.total_quantity) || 0;
    });
    return m;
  }, [fgBalances]);

  const stockDetailRows = useMemo(() => {
    if (!stockDetailProductId) return [];
    return (fgBalances.by_order_product || [])
      .filter((r) => Number(r.product_id) === Number(stockDetailProductId))
      .slice()
      .sort((a, b) => (Number(b.quantity) || 0) - (Number(a.quantity) || 0));
  }, [fgBalances, stockDetailProductId]);

  const shipDraftTotal = useMemo(() => {
    return Object.values(shipDraft).reduce((sum, v) => {
      const n = parseInt(v, 10);
      return sum + (Number.isFinite(n) && n > 0 ? n : 0);
    }, 0);
  }, [shipDraft]);

  const applyShipAllocation = () => {
    const cleaned = {};
    let total = 0;
    Object.entries(shipDraft).forEach(([oid, val]) => {
      const n = parseInt(val, 10);
      if (Number.isFinite(n) && n > 0) {
        cleaned[oid] = n;
        total += n;
      }
    });
    setLineRows((prev) =>
      prev.map((r) =>
        r.tempKey === stockDetailRowTempKey ? { ...r, to_ship_allocations: cleaned, to_ship: total } : r,
      ),
    );
    setStockDetailOpen(false);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [oRes, cRes, pRes] = await Promise.all([
        customerOrderService.getAll(),
        customerDirectoryService.getAll({ is_active: true }),
        productService.getAll(),
      ]);
      const normalize = (res) =>
        Array.isArray(res.data?.results) ? res.data.results : Array.isArray(res.data) ? res.data : [];
      setOrders(normalize(oRes));
      setCustomers(normalize(cRes));
      setProducts(normalize(pRes));
    } catch (error) {
      console.error(error);
      alert('Помилка завантаження замовлень');
    } finally {
      setLoading(false);
    }
  };

  const customerOptions = useMemo(
    () =>
      (customers || []).map((c) => ({
        id: c.id,
        label: c.edrpou ? `${c.name} (ЄДРПОУ ${c.edrpou})` : c.name,
        name: c.name,
      })),
    [customers],
  );

  const productOptions = useMemo(
    () =>
      products.map((p) => ({
        id: p.id,
        article: p.article || '',
        name: p.name,
        unit_price: p.unit_price,
        label: productLabelWithoutLeadingArticle(p.name, p.article) || p.article || '',
        image_url: p.image_url || p.image || null,
      })),
    [products]
  );

  /** Лише вироби, додані обраному замовнику в довіднику (його прайс-лист). */
  const customerProductOptions = useMemo(() => {
    const prices = customerCache?.product_prices || [];
    if (!prices.length) return productOptions;
    const allowedIds = new Set(prices.map((p) => Number(p.product)));
    return productOptions.filter((p) => allowedIds.has(Number(p.id)));
  }, [productOptions, customerCache]);

  const customerProductByArticle = useMemo(() => {
    const map = new Map();
    customerProductOptions.forEach((p) => {
      const k = String(p.article || '').trim().toLowerCase();
      if (k) map.set(k, p);
    });
    return map;
  }, [customerProductOptions]);

  /** Ціна виробу замовника за обраним видом розрахунку (Форма 1/Форма 2), за замовчуванням — Форма 1. */
  const getCustomerFormPrice = (productId, calcForm) => {
    const prices = customerCache?.product_prices || [];
    const pp = prices.find((p) => Number(p.product) === Number(productId));
    if (!pp) return null;
    const value = calcForm === 'form2' ? pp.price_form2 : pp.price_form1;
    return value != null ? value : null;
  };

  const openDialog = (order = null) => {
    setCustomerCache(null);
    if (order) {
      setEditing(order.id);
      const rows = (order.lines || []).map((ln) => {
        const prodRow = products.find((p) => Number(p.id) === Number(ln.product));
        return {
          tempKey: nextLineKey(),
          id: ln.id,
          product: ln.product || '',
          product_article: ln.product_article || prodRow?.article || '',
          quantity: ln.quantity != null ? String(ln.quantity) : '',
          color: ln.color || '',
          price: ln.price != null ? String(ln.price) : '',
          calc_form: ln.calc_form || '',
          payment_method: ln.payment_method || '',
          shipped_quantity: Number(ln.shipped_quantity) || 0,
          production_status_display: ln.production_status_display || '',
          to_ship_allocations: {},
          to_ship: 0,
        };
      });
      setLineRows(rows.length ? rows : [emptyLineRow()]);
      setKitPackaging(order.kit_packaging || []);
      setAppliedKitInfo(null);
      setForm({
        number: order.number,
        customer: order.customer || '',
        customer_name: order.customer_name,
        order_date: order.order_date ? isoToDateInput(order.order_date) : todayDateInput(),
        deadline: order.deadline ? isoToDateInput(order.deadline) : '',
        payment_type: order.payment_type || 'cash',
        calc_form: (order.lines && order.lines[0]?.calc_form) || 'form1',
        payment_method: (order.lines && order.lines[0]?.payment_method) || '',
        shipping_method: order.shipping_method || 'self',
        unloading_address: order.unloading_address || '',
        warehouse: order.warehouse || '',
        comment: order.comment || '',
      });
    } else {
      setEditing(null);
      setLineRows([emptyLineRow()]);
      setKitPackaging([]);
      setAppliedKitInfo(null);
      setForm({
        number: '',
        customer: '',
        customer_name: '',
        order_date: todayDateInput(),
        deadline: '',
        payment_type: 'cash',
        calc_form: 'form1',
        payment_method: '',
        shipping_method: 'self',
        unloading_address: '',
        warehouse: '',
        comment: '',
      });
    }
    setDialogOpen(true);
  };

  useImperativeHandle(ref, () => ({
    openNewOrder: () => openDialog(null),
  }));

  const closeDialog = () => {
    priorOrdersReqRef.current += 1;
    setDialogOpen(false);
    setEditing(null);
    setCustomerCache(null);
    setPriorOrdersDialogOpen(false);
    setPriorOrdersList([]);
    setPriorOrdersLoading(false);
    setStockDetailOpen(false);
    setStockDetailProductId(null);
    setStockDetailLabel('');
    setStockDetailRowTempKey(null);
    setShipDraft({});
    setKitPackaging([]);
    setAppliedKitInfo(null);
  };

  const closePriorOrdersDialog = () => {
    priorOrdersReqRef.current += 1;
    setPriorOrdersDialogOpen(false);
    setPriorOrdersList([]);
    setPriorOrdersLoading(false);
  };

  const fetchAllOrdersForCustomer = async (customerId) => {
    const acc = [];
    let page = 1;
    for (;;) {
      const res = await customerOrderService.getAll({
        customer: customerId,
        ordering: '-order_date',
        page,
      });
      const data = res.data;
      const chunk = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
          ? data
          : [];
      acc.push(...chunk);
      if (!data?.next || chunk.length === 0) break;
      page += 1;
    }
    return acc;
  };

  const lineRowsHaveUserContent = useMemo(() => {
    return lineRows.some(
      (r) =>
        r.product ||
        (String(r.quantity || '').trim() !== '' && r.quantity != null) ||
        (r.price !== '' && r.price != null),
    );
  }, [lineRows]);

  /**
   * Частка ширини таблиці під «Артикул» зростає з довжиною тексту; «Виріб» зменшується
   * (сума «Артикул» + «Виріб» = 36%, решта колонок без змін — без горизонтального скролу).
   */
  const orderLinesArticleColPct = useMemo(() => {
    const maxLen = lineRows.reduce(
      (m, r) => Math.max(m, String(r.product_article ?? '').length),
      0,
    );
    if (maxLen <= 6) return 8;
    const extra = Math.ceil((maxLen - 6) / 2);
    return Math.min(18, 8 + extra);
  }, [lineRows]);

  const orderLinesProductColPct = useMemo(
    () => 36 - orderLinesArticleColPct,
    [orderLinesArticleColPct],
  );

  const openPriorOrdersDialog = async () => {
    if (!form.customer) return;
    const reqId = priorOrdersReqRef.current + 1;
    priorOrdersReqRef.current = reqId;
    setPriorOrdersDialogOpen(true);
    setPriorOrdersLoading(true);
    setPriorOrdersList([]);
    try {
      const all = await fetchAllOrdersForCustomer(form.customer);
      if (priorOrdersReqRef.current !== reqId) return;
      const filtered = all.filter((o) => !editing || Number(o.id) !== Number(editing));
      setPriorOrdersList(filtered);
    } catch (e) {
      if (priorOrdersReqRef.current !== reqId) return;
      console.error(e);
      alert(e.response?.data?.detail || e.message || 'Помилка завантаження замовлень');
      setPriorOrdersDialogOpen(false);
    } finally {
      if (priorOrdersReqRef.current === reqId) setPriorOrdersLoading(false);
    }
  };

  const applyLinesFromPriorOrder = (order) => {
    const srcLines = order.lines || [];
    if (!srcLines.length) {
      alert('У цьому замовленні немає позицій');
      return;
    }
    if (lineRowsHaveUserContent) {
      if (!window.confirm('Поточні позиції в формі буде замінено. Продовжити?')) return;
    }
    const firstLine = srcLines[0];
    if (firstLine?.calc_form) {
      setForm((prev) => ({ ...prev, calc_form: firstLine.calc_form }));
    }
    if (firstLine?.payment_method) {
      setForm((prev) => ({ ...prev, payment_method: firstLine.payment_method }));
    }
    const rows = srcLines.map((ln) => {
      const prodRow = products.find((p) => Number(p.id) === Number(ln.product));
      return {
        tempKey: nextLineKey(),
        id: null,
        product: ln.product || '',
        product_article: ln.product_article || prodRow?.article || '',
        quantity: ln.quantity != null ? String(ln.quantity) : '',
        color: ln.color || '',
        price: ln.price != null ? String(ln.price) : '',
        shipped_quantity: 0,
        production_status_display: '',
        to_ship_allocations: {},
        to_ship: 0,
      };
    });
    setLineRows(rows.length ? rows : [emptyLineRow()]);
    closePriorOrdersDialog();
  };

  useEffect(() => {
    if (!form.customer) {
      setCustomerCache(null);
      return;
    }
    let cancelled = false;
    customerDirectoryService
      .getById(form.customer)
      .then((res) => {
        if (!cancelled) setCustomerCache(res.data);
      })
      .catch(() => {
        if (!cancelled) setCustomerCache(null);
      });
    return () => {
      cancelled = true;
    };
  }, [form.customer]);

  useEffect(() => {
    setLineRows((rows) =>
      rows.map((r) => {
        if (!r.product || (r.price !== '' && r.price != null)) return r;
        const formPrice = getCustomerFormPrice(r.product, r.calc_form || 'form1');
        if (formPrice != null) return { ...r, price: String(formPrice) };
        const prod = products.find((p) => Number(p.id) === Number(r.product));
        if (prod?.unit_price != null && prod.unit_price !== '') {
          return { ...r, price: String(prod.unit_price) };
        }
        return r;
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCache, products]);

  useEffect(() => {
    if (!customerCache || editing != null) return;
    setForm((prev) => ({
      ...prev,
      shipping_method: customerCache.shipping_method || prev.shipping_method,
      unloading_address:
        !String(prev.unloading_address || '').trim() && customerCache.delivery_address
          ? customerCache.delivery_address
          : prev.unloading_address,
    }));
  }, [customerCache, editing]);

  const addLineRow = () => {
    setLineRows((prev) => [...prev, emptyLineRow()]);
  };

  const openKitDialog = () => {
    productKitService.getAll({ is_active: true })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setKits(list);
        const defaults = {};
        list.forEach((k) => { defaults[k.id] = 1; });
        setKitMultiplier(defaults);
      })
      .catch(() => setKits([]));
    setKitDialogOpen(true);
  };

  const applyKit = async (kit) => {
    if (!kit.items || kit.items.length === 0) { setKitDialogOpen(false); return; }
    const mult = Number(kitMultiplier[kit.id]) || 1;
    const productItems = kit.items.filter((i) => i.item_type === 'product' || i.product);
    const packItems = kit.items.filter((i) => i.item_type === 'packaging' && !i.product);

    // Якщо customerCache ще не завантажений — завантажуємо синхронно
    let cache = customerCache;
    if (!cache && form.customer) {
      try {
        const res = await customerDirectoryService.getById(form.customer);
        cache = res.data;
        setCustomerCache(cache);
      } catch (e) { /* ігноруємо */ }
    }

    // Ціна комплекту замовника (якщо є) — загальна на 1 комплект
    const kitCustomerPrices = cache?.kit_prices || [];
    const kitCustomerPrice = kitCustomerPrices.find((kp) => Number(kp.kit) === Number(kit.id));
    const defaultCalcForm = form.calc_form || 'form1';


    // Якщо є ціна комплекту — розподіляємо пропорційно між виробами за кількістю
    const totalQtyInKit = productItems.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
    const kitPriceTotal = kitCustomerPrice
      ? (defaultCalcForm === 'form2' ? kitCustomerPrice.price_form2 : kitCustomerPrice.price_form1)
      : null;
    const pricePerUnit = (kitPriceTotal != null && totalQtyInKit > 0)
      ? kitPriceTotal / totalQtyInKit
      : null;

    const newRows = productItems.map((item) => {
      const prod = productOptions.find((p) => p.id === item.product);
      let resolvedPrice;
      if (pricePerUnit != null) {
        // Ціна з прайсу комплекту замовника
        resolvedPrice = pricePerUnit;
      } else {
        // Пріоритет: ціна замовника по виробу → базова unit_price
        const cacheProductPrices = cache?.product_prices || [];
        const cacheProductPrice = cacheProductPrices.find((p) => Number(p.product) === Number(item.product));
        const customerProductPrice = cacheProductPrice
          ? (defaultCalcForm === 'form2' ? cacheProductPrice.price_form2 : cacheProductPrice.price_form1)
          : getCustomerFormPrice(item.product, defaultCalcForm);
        resolvedPrice = customerProductPrice != null
          ? customerProductPrice
          : (prod?.unit_price != null ? prod.unit_price : null);
      }
      return {
        ...emptyLineRow(),
        product: item.product,
        product_article: prod?.article || '',
        price: resolvedPrice != null ? String(Math.round(resolvedPrice * 100) / 100) : '',
        quantity: item.quantity * mult,
      };
    });
    const newPackaging = packItems.map((item) => ({
      packaging_material_id: item.packaging_material,
      name: item.product_name || item.packaging_material_name || item.name || '',
      quantity: item.quantity * mult,
    }));
    setLineRows((prev) => {
      const hasContent = prev.some((r) => r.product || r.quantity);
      return hasContent ? [...prev, ...newRows] : newRows;
    });
    setKitPackaging((prev) => [...prev, ...newPackaging]);
    if (kitPriceTotal != null) {
      setAppliedKitInfo({ kitId: kit.id, mult, totalQtyInKit });
    }
    setKitDialogOpen(false);
  };

  const removeLineRow = (tempKey) => {
    setLineRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.tempKey !== tempKey)));
  };

  const updateLineRow = (tempKey, field, value) => {
    setLineRows((prev) => prev.map((r) => (r.tempKey === tempKey ? { ...r, [field]: value } : r)));
  };

  const handleSave = async () => {
    if (!form.number.trim()) {
      alert('Вкажіть номер замовлення');
      return;
    }
    if (!form.customer) {
      alert('Оберіть замовника з довідника');
      return;
    }
    const lines = [];
    for (let i = 0; i < lineRows.length; i += 1) {
      const r = lineRows[i];
      if (!r.product) continue;
      const qty = parseInt(r.quantity, 10);
      if (!Number.isFinite(qty) || qty < 1) {
        alert(`Позиція ${i + 1}: вкажіть кількість не менше 1`);
        return;
      }
      const row = {
        product: Number(r.product),
        quantity: qty,
        color: r.color || '',
        price: r.price !== '' && r.price != null ? parseFloat(r.price) : null,
        calc_form: form.calc_form || '',
        payment_method: form.payment_method || '',
        sort_order: lines.length,
      };
      if (r.id) row.id = r.id;
      lines.push(row);
    }
    if (!lines.length) {
      alert('Додайте хоча б одну позицію з виробом і кількістю');
      return;
    }
    const payload = {
      number: form.number,
      customer: form.customer ? Number(form.customer) : null,
      customer_name: form.customer_name,
      order_date: form.order_date ? dateInputToIso(form.order_date) : dateInputToIso(todayDateInput()),
      deadline: form.deadline ? dateInputToIso(form.deadline) : null,
      payment_type: form.payment_type,
      shipping_method: form.shipping_method,
      unloading_address: form.unloading_address,
      warehouse: form.warehouse,
      comment: form.comment,
      kit_packaging: kitPackaging,
      lines,
    };
    try {
      let orderId = editing;
      if (editing) {
        await customerOrderService.update(editing, payload);
      } else {
        const res = await customerOrderService.create(payload);
        orderId = res.data?.id;
      }
      // Заявка на закупівлю: при створенні CO превʼю не persist-ить (немає id) —
      // після збереження створюємо/оновлюємо заявку окремо.
      if (orderId) {
        const shortageLines = lineRows
          .filter((r) => r.product && String(r.quantity || '').trim() !== '')
          .map((r, i) => {
            const q = parseInt(String(r.quantity).replace(/\s/g, ''), 10);
            return {
              tempKey: r.tempKey || String(i),
              product: r.product,
              quantity: Number.isFinite(q) && q >= 1 ? q : 0,
            };
          })
          .filter((l) => l.quantity >= 1);
        if (shortageLines.length) {
          try {
            await customerOrderService.materialShortagePreview(shortageLines, {
              persistPurchaseRequest: true,
              customerOrderId: orderId,
            });
          } catch (e) {
            console.warn('Не вдалося створити заявку на закупівлю з нестачі', e);
          }
        }
      }
      closeDialog();
      loadData();
    } catch (error) {
      console.error(error);
      const msg =
        error.response?.data &&
        (typeof error.response.data === 'string'
          ? error.response.data
          : JSON.stringify(error.response.data, null, 2));
      alert(`Помилка збереження:\n${msg || error.message || 'Невідома помилка'}`);
    }
  };

  const productionStatusOptions = useMemo(() => {
    const set = new Set();
    (orders || []).forEach((o) => {
      if (o.production_status_display) set.add(o.production_status_display);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [orders]);

  const shipmentStatusOptions = useMemo(() => {
    const set = new Set();
    (orders || []).forEach((o) => {
      if (o.shipment_status_display) set.add(o.shipment_status_display);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    return (orders || []).filter((o) => {
      if (productionStatus && o.production_status_display !== productionStatus) return false;
      if (shipmentStatus && o.shipment_status_display !== shipmentStatus) return false;
      if (customerFilter && Number(o.customer) !== Number(customerFilter)) return false;
      if (dateFromFilter) {
        const d = o.order_date ? new Date(o.order_date) : null;
        const from = new Date(`${dateFromFilter}T00:00:00`);
        if (!d || d < from) return false;
      }
      if (dateToFilter) {
        const d = o.order_date ? new Date(o.order_date) : null;
        const to = new Date(`${dateToFilter}T23:59:59`);
        if (!d || d > to) return false;
      }
      if (!q) return true;
      const lineHay = (o.lines || [])
        .map((ln) => [ln.product_name, ln.product_article].filter(Boolean).join(' '))
        .join(' ');
      const hay = [
        o.number,
        o.customer_name,
        o.lines_summary,
        lineHay,
        o.production_status_display,
        o.shipment_status_display,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [
    orders,
    search,
    productionStatus,
    shipmentStatus,
    customerFilter,
    dateFromFilter,
    dateToFilter,
  ]);

  const orderLineRows = useMemo(() => {
    const rows = [];
    for (const o of filteredOrders) {
      const lines = o.lines || [];
      if (lines.length === 0) {
        rows.push({ key: `${o.id}-empty`, order: o, line: null });
        continue;
      }
      for (const l of lines) {
        rows.push({ key: `${o.id}-${l.id}`, order: o, line: l });
      }
    }
    return rows;
  }, [filteredOrders]);

  const orderLinesTotal = useMemo(() => {
    let sum = 0;
    let has = false;
    for (const r of lineRows) {
      const q = parseInt(r.quantity, 10);
      const unit = r.price === '' || r.price == null ? NaN : Number(r.price);
      if (!Number.isFinite(q) || q < 1 || !Number.isFinite(unit)) continue;
      sum += q * unit;
      has = true;
    }
    return has ? sum : null;
  }, [lineRows]);

  const handleDelete = async (id) => {
    if (!window.confirm('Видалити замовлення?')) return;
    try {
      await customerOrderService.delete(id);
      loadData();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.detail || error.message || 'Помилка видалення');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(6, 1fr)',
            },
            gap: 1.5,
            alignItems: 'flex-start',
          }}
        >
          <TextField
            size="small"
            placeholder="Пошук"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <TextField
            select
            size="small"
            label="Замовник"
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            fullWidth
          >
            <MenuItem value="">Усі</MenuItem>
            {customerOptions.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Дата від"
            type="date"
            value={dateFromFilter}
            onChange={(e) => setDateFromFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            size="small"
            label="Дата до"
            type="date"
            value={dateToFilter}
            onChange={(e) => setDateToFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            select
            size="small"
            label="Статус виробництва"
            value={productionStatus}
            onChange={(e) => setProductionStatus(e.target.value)}
            fullWidth
          >
            <MenuItem value="">Усі</MenuItem>
            {productionStatusOptions.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Статус відвантаження"
            value={shipmentStatus}
            onChange={(e) => setShipmentStatus(e.target.value)}
            fullWidth
          >
            <MenuItem value="">Усі</MenuItem>
            {shipmentStatusOptions.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box display="flex" justifyContent="flex-end" mt={1.5}>
          <Button
            size="small"
            onClick={() => {
              setSearch('');
              setProductionStatus('');
              setShipmentStatus('');
              setCustomerFilter('');
              setDateFromFilter('');
              setDateToFilter('');
            }}
          >
            Скинути фільтри
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              <TableCell>Номер замовлення</TableCell>
              <TableCell>Замовник</TableCell>
              <TableCell>Назва виробу</TableCell>
              <TableCell>Дата замовлення</TableCell>
              <TableCell>Бажаний дедлайн</TableCell>
              <TableCell>Статус виробництва</TableCell>
              <TableCell>Статус відвантаження</TableCell>
              <TableCell align="right">Кількість виробів</TableCell>
              <TableCell align="right">Сума</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderLineRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="textSecondary" sx={{ py: 3 }}>
                    Немає замовлень
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              orderLineRows.map(({ key, order: o, line: l }) => {
                const qty = l ? parseInt(l.quantity, 10) || 0 : 0;
                const price = l && l.price !== '' && l.price != null ? Number(l.price) : NaN;
                const amount = l && Number.isFinite(price) ? qty * price : null;
                return (
                  <TableRow key={key} hover>
                    <TableCell>{o.number}</TableCell>
                    <TableCell>{o.customer_name}</TableCell>
                    <TableCell>{l ? l.product_name : '—'}</TableCell>
                    <TableCell>
                      {formatOrderDateOnly(o.order_date)}
                    </TableCell>
                    <TableCell>
                      {formatOrderDateOnly(o.deadline)}
                    </TableCell>
                    <TableCell>{(l && l.production_status_display) || '—'}</TableCell>
                    <TableCell>{o.shipment_status_display || '—'}</TableCell>
                    <TableCell align="right">{qty}</TableCell>
                    <TableCell align="right">{formatOrderAmount(amount)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openDialog(o)} title="Редагувати">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(o.id)} title="Видалити">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} fullScreen>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {editing ? 'Редагувати замовлення' : 'Нове замовлення'}
          <IconButton onClick={closeDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 10 }}>
          <Box mt={1}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr', md: '1fr 2fr 1fr 1fr' },
                gap: 2,
                alignItems: 'flex-start',
                mb: 1,
              }}
            >
              <TextField
                label="Номер"
                fullWidth
                margin="normal"
                value={form.number}
                onChange={(e) => setForm((prev) => ({ ...prev, number: e.target.value }))}
              />
              <Autocomplete
                options={customerOptions}
                isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o.label || '')}
                value={customerOptions.find((c) => Number(c.id) === Number(form.customer)) || null}
                onChange={(_, opt) => {
                  if (!opt) {
                    setForm((prev) => ({ ...prev, customer: '', customer_name: '' }));
                    return;
                  }
                  setForm((prev) => ({
                    ...prev,
                    customer: opt.id,
                    customer_name: opt.name || prev.customer_name,
                  }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Замовник (довідник)" margin="normal" placeholder="Оберіть замовника" />
                )}
              />
              <TextField
                label="Дата замовлення"
                type="date"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={form.order_date}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, order_date: e.target.value }))
                }
              />
              <TextField
                label="Бажаний дедлайн"
                type="date"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={form.deadline}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, deadline: e.target.value }))
                }
              />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
                alignItems: 'flex-start',
                mb: 1,
              }}
            >
              <TextField
                select
                label="Вид розрахунку"
                fullWidth
                margin="normal"
                value={form.calc_form}
                onChange={(e) => {
                  const calcForm = e.target.value;
                  setForm((prev) => ({ ...prev, calc_form: calcForm }));
                  setLineRows((prev) => {
                    // Якщо замовлення з комплекту — перераховуємо ціну комплекту за новою формою
                    if (appliedKitInfo) {
                      const kitCustPrices = customerCache?.kit_prices || [];
                      const kcp = kitCustPrices.find((p) => Number(p.kit) === Number(appliedKitInfo.kitId));
                      if (kcp) {
                        const kitTotal = calcForm === 'form2' ? kcp.price_form2 : kcp.price_form1;
                        if (kitTotal != null && appliedKitInfo.totalQtyInKit > 0) {
                          const ppu = kitTotal / appliedKitInfo.totalQtyInKit;
                          return prev.map((r) =>
                            r.product ? { ...r, price: String(Math.round(ppu * 100) / 100) } : r,
                          );
                        }
                      }
                    }
                    // Інакше — стандартний прайс замовника по виробах
                    return prev.map((r) => {
                      if (!r.product) return r;
                      const formPrice = getCustomerFormPrice(r.product, calcForm);
                      return formPrice != null ? { ...r, price: String(formPrice) } : r;
                    });
                  });
                }}
              >
                {CALC_FORM_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Метод оплати"
                fullWidth
                margin="normal"
                value={form.payment_method}
                onChange={(e) => setForm((prev) => ({ ...prev, payment_method: e.target.value }))}
              >
                <MenuItem value="">—</MenuItem>
                {PAYMENT_METHOD_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            </Box>

            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
                flexWrap="wrap"
                gap={1}
              >
                <Typography variant="subtitle2">Вироби в замовленні</Typography>
                <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                  <Tooltip
                    title={
                      form.customer
                        ? 'Скопіювати вироби, кількості та ціни з обраного замовлення'
                        : 'Спочатку оберіть замовника в полі «Замовник (довідник)»'
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<HistoryIcon />}
                        onClick={openPriorOrdersDialog}
                        disabled={!form.customer}
                      >
                        З попереднього замовлення
                      </Button>
                    </span>
                  </Tooltip>
                  <Button size="small" startIcon={<AddIcon />} onClick={addLineRow}>
                    Додати позицію
                  </Button>
                  <Button size="small" variant="outlined" onClick={openKitDialog}>
                    З комплекту
                  </Button>
                </Box>
              </Box>
              {!materialShortagePreview.sufficient && materialShortagePreview.shortages.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                  <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
                    На вказані кількості не вистачає сировини/барвнику (з урахуванням резервів на складі):
                  </Typography>
                  <Box component="ul" sx={{ m: '6px 0 0', pl: 2.5 }}>
                    {materialShortagePreview.shortages.map((s, idx) => (
                      <Typography component="li" variant="body2" key={`${s.raw_material_id ?? 'r'}-${s.dye_id ?? 'd'}-${idx}`}>
                        <strong>{s.name}</strong>: потрібно {s.required} {s.unit || 'кг'}, доступно{' '}
                        {s.available} {s.unit || 'кг'}
                      </Typography>
                    ))}
                  </Box>
                  {materialShortagePreview.purchaseRequestId != null ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Заявку на закупівлю №{materialShortagePreview.purchaseRequestId} уже створено.{' '}
                      <RouterLink to={`/purchases?view=requests`}>Переглянути в «Заявках»</RouterLink>
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      При збереженні замовлення заявка на закупівлю створиться автоматично.
                    </Typography>
                  )}
                </Alert>
              )}
              <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                <Table
                  size="small"
                  sx={{
                    tableLayout: 'fixed',
                    width: '100%',
                    minWidth: 1000,
                    fontSize: '0.8125rem',
                    '& .MuiTableCell-root': { py: 0.5, px: 0.4, verticalAlign: 'middle' },
                    '& .MuiInputBase-root': { fontSize: 'inherit' },
                    '& .MuiInputBase-input': { fontSize: 'inherit' },
                    '& .MuiTableBody-root .MuiTableCell-root.order-lines-product-cell': {
                      verticalAlign: 'top',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          width: `${orderLinesArticleColPct}%`,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Артикул
                      </TableCell>
                      <TableCell
                        sx={{
                          width: `${orderLinesProductColPct}%`,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Виріб
                      </TableCell>
                      <TableCell sx={{ width: '6%', whiteSpace: 'nowrap' }} align="right">
                        На складі ГП
                      </TableCell>
                      <TableCell sx={{ width: '5%', whiteSpace: 'nowrap' }}>Кількість</TableCell>
                      <TableCell sx={{ width: '8%', whiteSpace: 'nowrap' }} align="right">
                        До відвантаження
                      </TableCell>
                      <TableCell sx={{ width: '9%', whiteSpace: 'nowrap' }}>Перевірка</TableCell>
                      <TableCell sx={{ width: '8%', whiteSpace: 'nowrap' }} align="right">
                        Ціна / од.
                      </TableCell>
                      <TableCell sx={{ width: '6%', whiteSpace: 'nowrap' }} align="right">
                        Сума
                      </TableCell>
                      <TableCell sx={{ width: 40, maxWidth: 40, p: '4px !important' }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineRows.map((row) => {
                      const rowProductOpt =
                        productOptions.find((p) => Number(p.id) === Number(row.product)) || null;
                      return (
                      <TableRow
                        key={row.tempKey}
                        sx={
                          materialShortagePreview.byLine[row.tempKey]
                            ? (theme) => ({
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.warning.dark, 0.35)}`,
                              })
                            : undefined
                        }
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <TextField
                            size="small"
                            fullWidth
                            value={row.product_article || ''}
                            placeholder="Артикул"
                            onChange={(e) => {
                              const article = e.target.value;
                              const key = String(article || '').trim().toLowerCase();
                              const exact = key ? customerProductByArticle.get(key) : null;
                              if (exact) {
                                setLineRows((prev) =>
                                  prev.map((r) => {
                                    if (r.tempKey !== row.tempKey) return r;
                                    const formPrice = getCustomerFormPrice(exact.id, r.calc_form || 'form1');
                                    return {
                                      ...r,
                                      product: exact.id,
                                      product_article: exact.article || article,
                                      price:
                                        formPrice != null
                                          ? String(formPrice)
                                          : exact.unit_price != null
                                          ? String(exact.unit_price)
                                          : '',
                                    };
                                  }),
                                );
                                setProductPickerOpenKey(null);
                                return;
                              }
                              updateLineRow(row.tempKey, 'product_article', article);
                              setProductPickerOpenKey(row.tempKey);
                            }}
                            onFocus={() => {
                              if ((row.product_article || '').trim()) setProductPickerOpenKey(row.tempKey);
                            }}
                          />
                        </TableCell>
                        <TableCell className="order-lines-product-cell">
                          <Autocomplete
                            size="small"
                            options={(() => {
                              const q = String(row.product_article || '').trim().toLowerCase();
                              if (!q) return customerProductOptions;
                              return customerProductOptions.filter((p) =>
                                String(p.article || '').trim().toLowerCase().includes(q),
                              );
                            })()}
                            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                            getOptionLabel={(o) => (typeof o === 'string' ? o : o.label || '')}
                            open={productPickerOpenKey === row.tempKey}
                            onOpen={() => setProductPickerOpenKey(row.tempKey)}
                            onClose={() => {
                              if (productPickerOpenKey === row.tempKey) setProductPickerOpenKey(null);
                            }}
                            value={rowProductOpt}
                            onChange={(_, opt) => {
                              const pid = opt ? opt.id : '';
                              const article = opt?.article || '';
                              setLineRows((prev) =>
                                prev.map((r) => {
                                  if (r.tempKey !== row.tempKey) return r;
                                  let next = {
                                    ...r,
                                    product: pid,
                                    product_article: article,
                                    shipped_quantity: 0,
                                    production_status_display: '',
                                  };
                                  if (pid) {
                                    const formPrice = getCustomerFormPrice(pid, next.calc_form || 'form1');
                                    if (formPrice != null) {
                                      next = { ...next, price: String(formPrice) };
                                    } else if (opt?.unit_price != null) {
                                      next = { ...next, price: String(opt.unit_price) };
                                    } else {
                                      next = { ...next, price: '' };
                                    }
                                  } else {
                                    next = { ...next, price: '' };
                                  }
                                  return next;
                                }),
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Оберіть виріб"
                                multiline
                                minRows={1}
                                maxRows={2}
                                InputProps={{
                                  ...params.InputProps,
                                  sx: {
                                    alignItems: 'flex-start',
                                    py: 0.75,
                                  },
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    ...(params.inputProps?.style || {}),
                                    overflow: 'auto',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'normal',
                                  },
                                }}
                              />
                            )}
                            sx={{
                              minWidth: 0,
                              width: '100%',
                              maxWidth: '100%',
                              '& .MuiAutocomplete-inputRoot': {
                                flexWrap: 'wrap',
                                alignItems: 'flex-start',
                              },
                              '& .MuiAutocomplete-input': {
                                minWidth: 0,
                                textOverflow: 'clip',
                              },
                              '& .MuiAutocomplete-inputRoot textarea': {
                                resize: 'none',
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {(() => {
                            const pid = Number(row.product);
                            if (!row.product || !Number.isFinite(pid)) {
                              return (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              );
                            }
                            const q = stockByProductId[pid] ?? 0;
                            const label =
                              productOptions.find((p) => Number(p.id) === pid)?.label || `виріб #${pid}`;
                            return (
                              <Tooltip title="Натисніть, щоб побачити залишок по виробничих замовленнях (підтверджені приходи)">
                                <span>
                                  <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => {
                                      setStockDetailProductId(pid);
                                      setStockDetailLabel(label);
                                      setStockDetailRowTempKey(row.tempKey);
                                      setShipDraft(row.to_ship_allocations || {});
                                      setStockDetailOpen(true);
                                    }}
                                    disabled={fgBalancesLoading}
                                    sx={{ minWidth: 0, fontWeight: 600, textTransform: 'none' }}
                                  >
                                    {fgBalancesLoading ? '…' : q}
                                  </Button>
                                </span>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            value={row.quantity}
                            onChange={(e) => updateLineRow(row.tempKey, 'quantity', e.target.value)}
                            inputProps={{ min: 1, step: 1 }}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                          <Typography variant="body2" fontWeight={row.to_ship > 0 ? 600 : 400}>
                            {row.to_ship > 0 ? row.to_ship : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {(() => {
                            const pid = Number(row.product);
                            const q = parseInt(row.quantity, 10);
                            if (!row.product || !Number.isFinite(pid) || !Number.isFinite(q) || q < 1) {
                              return (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              );
                            }
                            const stock = stockByProductId[pid] ?? 0;
                            const sufficient = q <= stock;
                            return (
                              <Box
                                sx={(theme) => ({
                                  px: 0.75,
                                  py: 0.5,
                                  borderRadius: 1,
                                  textAlign: 'center',
                                  bgcolor: alpha(
                                    sufficient ? theme.palette.success.main : theme.palette.warning.main,
                                    0.15,
                                  ),
                                  color: sufficient ? 'success.dark' : 'warning.dark',
                                  fontWeight: 600,
                                  fontSize: '0.6875rem',
                                  lineHeight: 1.3,
                                  whiteSpace: 'normal',
                                })}
                              >
                                {sufficient ? 'Є на складі' : 'Потрібне виробництво'}
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            value={row.price}
                            onChange={(e) => updateLineRow(row.tempKey, 'price', e.target.value)}
                            inputProps={{ min: 0, step: '0.01' }}
                            sx={{
                              maxWidth: '100%',
                              '& .MuiInputBase-input': {
                                textAlign: 'right',
                                fontVariantNumeric: 'tabular-nums',
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {(() => {
                              const q = parseInt(row.quantity, 10);
                              const unit = row.price === '' || row.price == null ? NaN : Number(row.price);
                              if (!Number.isFinite(q) || q < 1 || !Number.isFinite(unit)) return '—';
                              return (q * unit).toLocaleString('uk-UA', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              });
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeLineRow(row.tempKey)}
                            disabled={lineRows.length <= 1}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box display="flex" justifyContent="flex-end" alignItems="baseline" gap={1} mt={1.5} px={0.5}>
                <Typography variant="body2" color="text.secondary">
                  Загальна вартість позицій
                </Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {orderLinesTotal != null
                    ? orderLinesTotal.toLocaleString('uk-UA', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : '—'}
                </Typography>
              </Box>
            </Paper>

            <TextField
              label="Коментар"
              fullWidth
              margin="normal"
              multiline
              minRows={2}
              value={form.comment}
              onChange={(e) => setForm((prev) => ({ ...prev, comment: e.target.value }))}
            />

            {kitPackaging.length > 0 && (
              <Box sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: 'warning.light', borderRadius: 1, bgcolor: 'warning.50' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'warning.dark' }}>
                  📦 Упаковка з комплекту (не відображається в рядках, фіксується за замовленням)
                </Typography>
                {kitPackaging.map((p, i) => (
                  <Typography key={i} variant="body2">
                    {p.name || `Упаковка #${p.packaging_material_id}`} — {p.quantity} шт
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 3,
            py: 1.5,
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
          }}
        >
          <Button onClick={closeDialog}>Скасувати</Button>
          <Button variant="contained" onClick={handleSave}>
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={stockDetailOpen}
        onClose={() => setStockDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Залишок на складі готової продукції</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {stockDetailLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Вкажіть, скільки виробів відвантажити з кожного замовлення — сума потрапить у колонку «До
            відвантаження».
          </Typography>
          {stockDetailRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Немає залишку цього виробу на складі готової продукції (за поточними даними).
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Номер замовлення</TableCell>
                    <TableCell>Назва клієнта</TableCell>
                    <TableCell>Дедлайн</TableCell>
                    <TableCell align="right">Кількість</TableCell>
                    <TableCell align="right" width={140}>
                      Відвантажити
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockDetailRows.map((r, i) => (
                    <TableRow key={`${r.order_id ?? 'none'}-${r.product_id}-${i}`}>
                      <TableCell>{r.customer_order_number || '—'}</TableCell>
                      <TableCell>{r.customer_name || '—'}</TableCell>
                      <TableCell>{formatOrderDateOnly(r.deadline)}</TableCell>
                      <TableCell align="right">{r.quantity}</TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={shipDraft[r.order_id] ?? ''}
                          onChange={(e) =>
                            setShipDraft((prev) => ({ ...prev, [r.order_id]: e.target.value }))
                          }
                          inputProps={{ min: 0, max: r.quantity, step: 1 }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {stockDetailRows.length > 0 && (
            <Box display="flex" justifyContent="flex-end" gap={1} mt={1.5}>
              <Typography variant="body2" color="text.secondary">
                Разом до відвантаження:
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {shipDraftTotal}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStockDetailOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={applyShipAllocation} disabled={stockDetailRows.length === 0}>
            Застосувати
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={priorOrdersDialogOpen}
        onClose={closePriorOrdersDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Попередні замовлення цього замовника</DialogTitle>
        <DialogContent>
          {priorOrdersLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : priorOrdersList.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              Немає інших збережених замовлень для цього запису довідника (або ще не створювали).
            </Typography>
          ) : (
            <TableContainer sx={{ mt: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Номер</TableCell>
                    <TableCell>Дата</TableCell>
                    <TableCell>Позиції</TableCell>
                    <TableCell align="right" width={120}>
                      Дія
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {priorOrdersList.map((o) => (
                    <TableRow key={o.id} hover>
                      <TableCell>{o.number}</TableCell>
                      <TableCell>
                        {formatOrderDateOnly(o.order_date)}
                      </TableCell>
                      <TableCell>{o.lines_summary || '—'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="contained" onClick={() => applyLinesFromPriorOrder(o)}>
                          Обрати
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePriorOrdersDialog} disabled={priorOrdersLoading}>
            Закрити
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kit selection dialog */}
      <Dialog open={kitDialogOpen} onClose={() => setKitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Оберіть комплект виробів</DialogTitle>
        <DialogContent dividers>
          {kits.length === 0 ? (
            <Typography color="text.secondary">Немає активних комплектів. Створіть їх у розділі "Комплекти виробів".</Typography>
          ) : (
            kits.map((kit) => {
              const mult = Number(kitMultiplier[kit.id]) || 1;
              const productItems = (kit.items || []).filter((i) => i.item_type === 'product' || i.product);
              const packItems = (kit.items || []).filter((i) => i.item_type === 'packaging' && !i.product);
              return (
                <Box key={kit.id} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography fontWeight={600}>{kit.name}</Typography>
                  {kit.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{kit.description}</Typography>}

                  {/* Кількість комплектів */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                    <Typography variant="body2" sx={{ flexShrink: 0 }}>Кількість комплектів:</Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={kitMultiplier[kit.id] ?? 1}
                      onChange={(e) => setKitMultiplier((prev) => ({ ...prev, [kit.id]: Math.max(1, Number(e.target.value)) }))}
                      inputProps={{ min: 1 }}
                      sx={{ width: 80 }}
                    />
                  </Box>

                  {/* Вироби */}
                  {productItems.length > 0 && (
                    <>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>Вироби:</Typography>
                      <Box component="ul" sx={{ m: '2px 0 6px', pl: 2 }}>
                        {productItems.map((item) => (
                          <Typography component="li" variant="body2" key={item.id}>
                            {item.product_name} — {item.quantity} шт
                            {mult > 1 && <Typography component="span" variant="body2" color="primary.main" sx={{ ml: 1 }}>
                              → {item.quantity * mult} шт
                            </Typography>}
                          </Typography>
                        ))}
                      </Box>
                    </>
                  )}

                  {/* Упаковка (показуємо, але не додаємо в рядки) */}
                  {packItems.length > 0 && (
                    <>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.main' }}>Упаковка (не додається в рядки):</Typography>
                      <Box component="ul" sx={{ m: '2px 0 6px', pl: 2 }}>
                        {packItems.map((item) => (
                          <Typography component="li" variant="body2" key={item.id} color="text.secondary">
                            {item.product_name} — {item.quantity} шт
                            {mult > 1 && <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                              → {item.quantity * mult} шт
                            </Typography>}
                          </Typography>
                        ))}
                      </Box>
                    </>
                  )}

                  <Button size="small" variant="contained" onClick={() => applyKit(kit)}>
                    Додати {productItems.length} виріб(и) з {mult} комплект(ів)
                  </Button>
                </Box>
              );
            })
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKitDialogOpen(false)}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default CustomerOrdersPage;

